import React from "react";
import {
  Box,
  Button,
  CardContent,
  Grid,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import SaveIcon from "@mui/icons-material/Save";
import color from "../../components/utils/Colors";
import { TabPanel } from "./Settings";

// ── Validation schema ──────────────────────────────────────────────────────────
const preferenceSchema = Yup.object({
  language: Yup.string()
    .oneOf(["en", "es", "fr", "de", "hi", "pt"], "Please select a valid language")
    .required("Language is required"),
  theme: Yup.string()
    .oneOf(["light", "dark", "auto"], "Please select a valid theme")
    .required("Theme is required"),
  fontSize: Yup.string()
    .oneOf(["small", "medium", "large"], "Please select a valid font size")
    .required("Font size is required"),
});

// ── Props ──────────────────────────────────────────────────────────────────────
interface PreferenceValues {
  language: string;
  theme: string;
  fontSize: string;
}

interface PreferenceTabProps {
  currentTab: number;
  initialValues: PreferenceValues;
  onSave: (values: PreferenceValues) => void;
}

// ── Component ──────────────────────────────────────────────────────────────────
const PreferenceTab: React.FC<PreferenceTabProps> = ({
  currentTab,
  initialValues,
  onSave,
}) => {
  return (
    <TabPanel value={currentTab} index={3}>
      <CardContent sx={{ padding: "30px" }}>
        <Typography
          variant="h6"
          sx={{
            color: color.textColor,
            fontWeight: "bold",
            marginBottom: "20px",
          }}
        >
          Language & Display
        </Typography>

        <Formik
          initialValues={initialValues}
          validationSchema={preferenceSchema}
          enableReinitialize
          onSubmit={(values) => onSave(values)}
        >
          {({ values, setFieldValue, isSubmitting, errors, touched }) => (
            <Form>
              <Grid container spacing={3} sx={{ marginBottom: "30px" }}>
                {/* Language */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666", marginBottom: "8px" }}
                  >
                    Language
                  </Typography>
                  <Select
                    fullWidth
                    value={values.language}
                    onChange={(e) => setFieldValue("language", e.target.value)}
                    error={touched.language && Boolean(errors.language)}
                  >
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="es">Spanish</MenuItem>
                    <MenuItem value="fr">French</MenuItem>
                    <MenuItem value="de">German</MenuItem>
                    <MenuItem value="hi">Hindi</MenuItem>
                    <MenuItem value="pt">Portuguese</MenuItem>
                  </Select>
                  {touched.language && errors.language && (
                    <Typography
                      variant="body2"
                      sx={{ color: "#d32f2f", mt: 0.5 }}
                    >
                      {errors.language}
                    </Typography>
                  )}
                </Grid>

                {/* Theme */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666", marginBottom: "8px" }}
                  >
                    Theme
                  </Typography>
                  <Select
                    fullWidth
                    value={values.theme}
                    onChange={(e) => setFieldValue("theme", e.target.value)}
                    error={touched.theme && Boolean(errors.theme)}
                  >
                    <MenuItem value="light">Light</MenuItem>
                    <MenuItem value="dark">Dark</MenuItem>
                    <MenuItem value="auto">Auto (System)</MenuItem>
                  </Select>
                  {touched.theme && errors.theme && (
                    <Typography
                      variant="body2"
                      sx={{ color: "#d32f2f", mt: 0.5 }}
                    >
                      {errors.theme}
                    </Typography>
                  )}
                </Grid>

                {/* Font Size */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666", marginBottom: "8px" }}
                  >
                    Font Size
                  </Typography>
                  <Select
                    fullWidth
                    value={values.fontSize}
                    onChange={(e) => setFieldValue("fontSize", e.target.value)}
                    error={touched.fontSize && Boolean(errors.fontSize)}
                  >
                    <MenuItem value="small">Small</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="large">Large</MenuItem>
                  </Select>
                  {touched.fontSize && errors.fontSize && (
                    <Typography
                      variant="body2"
                      sx={{ color: "#d32f2f", mt: 0.5 }}
                    >
                      {errors.fontSize}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={isSubmitting}
                sx={{
                  background: color.firstColor,
                  color: "white",
                  textTransform: "none",
                  fontWeight: "bold",
                  "&:hover": { background: color.forthColor },
                }}
              >
                Save Preferences
              </Button>
            </Form>
          )}
        </Formik>
      </CardContent>
    </TabPanel>
  );
};

export default PreferenceTab;